<template>
  <div class="dashboard-page">
    <!-- Dashboard Header -->
    <div class="dashboard-header">
      <div class="header-left">
        <h1 class="page-title">{{ greetingMessage }}</h1>
      </div>
      <div class="header-actions">
        <!-- Dashboard Toolbar (inline) -->
        <DashboardToolbar
          v-if="dashboardGridRef"
          :current-size="dashboardGridRef.layout.size"
          :hidden-widgets="dashboardGridRef.hiddenWidgets"
          :hidden-charts="dashboardGridRef.hiddenCharts"
          :hidden-stats="hiddenHeroStatsWithMeta"
          @size-change="onGridSizeChange"
          @add-widget="onGridAddWidget"
          @add-chart="onGridAddChart"
          @add-stat="onRestoreHeroStat"
          @reset-layout="onGridResetLayout"
        />
        <b-button-group class="view-toggle">
          <b-button variant="gray" class="active" title="Block View">
            <i class="fa fa-th-large" />
          </b-button>
          <router-link :to="{ name: 'AdminByEmployeePage'}" tag="span">
            <b-button variant="gray" title="Column View by Employee">
              <i class="fa fa-columns" />
            </b-button>
          </router-link>
        </b-button-group>
      </div>
    </div>

    <AdminPage2Skeleton v-if="busy" />

    <!-- Hero Stats Bar (Tier 1: Draggable horizontally) -->
    <HeroStatsBar
      v-if="allSettings"
      v-show="!busy"
      :stats="heroStats"
      :show-tier-label="false"
      @stat-click="handleStatClick"
      @order-change="handleStatsOrderChange"
      @stat-remove="handleStatRemove"
    />

    <!-- Customizable Dashboard Widgets Grid (Charts, Widgets, and Insights all in one grid) -->
    <div class="dashboard-widgets-container" v-if="allSettings" v-show="!busy">
      <DashboardGrid
        ref="dashboardGrid"
        :site-id="siteID"
        :user-id="userID"
        :site-name="user.SiteName"
        :messaging-enabled="messagingFeatureEnabled"
        :forms-enabled="formsPaidFor"
        :hide-toolbar="true"
        :weekly-activity-data="weeklyActivityData"
        :weekly-labels="weeklyLabels"
        :total-weekly-activity="totalWeeklyActivity"
        :weekly-trend="weeklyTrend"
        :activity-inline-stats="activityInlineStats"
        :recent-activity="recentActivity"
        :appointments-data="appointmentsData"
        :total-appointments="totalAppointments"
        :appointments-trend="appointmentsTrend"
        :appointments-inline-stats="appointmentsInlineStats"
        :recent-appointments="recentAppointments"
        :campaigns-data="campaignsData"
        :total-campaigns-sent="totalCampaignsSent"
        :campaigns-trend="campaignsTrend"
        :sample-recent-campaigns="sampleRecentCampaigns"
        @period-change="handlePeriodChange"
        @view-all-activity="handleViewAllActivity"
        @activity-action="handleActivityAction"
        @campaign-item-click="handleCampaignItemClick"
      />
    </div>

    <!-- Data Table Widgets Grid (Original Layout) -->
    <div class="widgets-grid" v-if="allSettings && employees">
      <!-- Overdue Recall Widget -->
      <DashboardWidget
        :restricted="!$can('view', 'overdue_recall') && !$can('view', 'all')"
        subject="overdue_recall"
        title="Overdue Recall"
        icon="fa fa-phone"
        :show-progress="allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL && allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue > 0"
        :progress-label="`Daily Goal: ${allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL ? allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue : 0} Calls`"
        :progress-value="parseInt(overdueRecallDataPercentCompleted) || 0"
        :is-empty="!overdueRecallDataToday || overdueRecallDataToday.length === 0"
        empty-icon="fa fa-phone"
        empty-title="No overdue recall"
        empty-text="All caught up!"
      >
        <template #header-actions>
          <div v-if="overdueRecallResponsible" class="responsible-person" v-b-tooltip.hover :title="overdueRecallResponsible.all.join(', ')">
            <span class="person-icon"><i class="fa fa-user"></i></span>
            <span>{{ overdueRecallResponsible.first }}<span v-if="overdueRecallResponsible.count > 1" class="responsible-count">&nbsp;+{{ overdueRecallResponsible.count - 1 }}</span></span>
          </div>
        </template>
        <div class="legacy-widget">
          <AdminTable v-if="overdueRecallDataToday" :data="overdueRecallDataToday" :columns="overdueRecallColumns" :options="options" :extra="getExtraParams('overdue-recall')" />
        </div>
      </DashboardWidget>

      <!-- Unscheduled Treatment Widget -->
      <DashboardWidget
        :restricted="!$can('view', 'unscheduled_treatment') && !$can('view', 'all')"
        subject="unscheduled_treatment"
        title="Unscheduled Treatment"
        icon="fa fa-calendar-o"
        :show-progress="allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL && allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue > 0"
        :progress-label="`Daily Goal: ${allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL ? allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue : 0} Calls`"
        :progress-value="parseInt(unscheduledTreatmentDataPercentCompleted) || 0"
        :is-empty="!unscheduledTreatmentDataToday || unscheduledTreatmentDataToday.length === 0"
        empty-icon="fa fa-calendar-check-o"
        empty-title="No unscheduled treatment"
        empty-text="All caught up!"
      >
        <template #header-actions>
          <div v-if="unscheduledTreatmentResponsible" class="responsible-person" v-b-tooltip.hover :title="unscheduledTreatmentResponsible.all.join(', ')">
            <span class="person-icon"><i class="fa fa-user"></i></span>
            <span>{{ unscheduledTreatmentResponsible.first }}<span v-if="unscheduledTreatmentResponsible.count > 1" class="responsible-count">&nbsp;+{{ unscheduledTreatmentResponsible.count - 1 }}</span></span>
          </div>
        </template>
        <div class="legacy-widget">
          <AdminTable v-if="unscheduledTreatmentDataToday" :data="unscheduledTreatmentDataToday" :columns="unscheduledTreatmentColumns" :options="options" :extra="getExtraParams('unscheduled-treatment')" />
        </div>
      </DashboardWidget>
    </div>

    <!-- Second Widgets Grid: Broken Appointments & Outstanding Claims -->
    <div class="widgets-grid" v-if="allSettings && employees">
      <!-- Broken Appointments Widget -->
      <DashboardWidget
        :restricted="!$can('view', 'broken_appointment') && !$can('view', 'all')"
        subject="broken_appointment"
        title="Broken Appointments"
        icon="fa fa-calendar-times-o"
        :is-empty="!brokenAppointmentsDataToday || brokenAppointmentsDataToday.length === 0"
        empty-icon="fa fa-calendar-check-o"
        empty-title="No broken appointments"
        empty-text="All caught up!"
      >
        <template #header-actions>
          <div v-if="brokenAppointmentResponsible" class="responsible-person" v-b-tooltip.hover :title="brokenAppointmentResponsible.all.join(', ')">
            <span class="person-icon"><i class="fa fa-user"></i></span>
            <span>{{ brokenAppointmentResponsible.first }}<span v-if="brokenAppointmentResponsible.count > 1" class="responsible-count">&nbsp;+{{ brokenAppointmentResponsible.count - 1 }}</span></span>
          </div>
        </template>
        <div class="legacy-widget">
          <AdminTable v-if="brokenAppointmentsDataToday" :data="brokenAppointmentsDataToday" :columns="brokenAppointmentsColumns" :options="options" :extra="getExtraParams('broken-appointments')" />
        </div>
      </DashboardWidget>

      <!-- Outstanding Claims Widget -->
      <DashboardWidget
        :restricted="!$can('view', 'outstanding_claim') && !$can('view', 'all')"
        subject="outstanding_claim"
        title="Outstanding Claims"
        icon="fa fa-file-text-o"
        :is-empty="!outstandingClaimsDataToday || outstandingClaimsDataToday.length === 0"
        empty-icon="fa fa-file-o"
        empty-title="No outstanding claims"
        empty-text="All caught up!"
      >
        <template #header-actions>
          <div v-if="outstandingClaimResponsible" class="responsible-person" v-b-tooltip.hover :title="outstandingClaimResponsible.all.join(', ')">
            <span class="person-icon"><i class="fa fa-user"></i></span>
            <span>{{ outstandingClaimResponsible.first }}<span v-if="outstandingClaimResponsible.count > 1" class="responsible-count">&nbsp;+{{ outstandingClaimResponsible.count - 1 }}</span></span>
          </div>
        </template>
        <div class="legacy-widget">
          <AdminTable v-if="outstandingClaimsDataToday" :data="outstandingClaimsDataToday" :columns="outstandingClaimsColumns" :options="outstandingClaimsOptions" :extra="getExtraParams('outstanding-claims')" />
        </div>
      </DashboardWidget>
    </div>

    <!-- Third Widgets Grid: Patient Balances & Outstanding Pre-Auths -->
    <div class="widgets-grid" v-if="allSettings && employees">
      <!-- Patient Balances Widget -->
      <DashboardWidget
        :restricted="!$can('view', 'patient_balances') && !$can('view', 'all')"
        subject="patient_balances"
        title="Patient Balances"
        icon="fa fa-usd"
        :is-empty="!patientBalancesDataToday || patientBalancesDataToday.length === 0"
        empty-icon="fa fa-usd"
        empty-title="No outstanding balances"
        empty-text="All caught up!"
      >
        <template #header-actions>
          <div v-if="patientBalancesResponsible" class="responsible-person" v-b-tooltip.hover :title="patientBalancesResponsible.all.join(', ')">
            <span class="person-icon"><i class="fa fa-user"></i></span>
            <span>{{ patientBalancesResponsible.first }}<span v-if="patientBalancesResponsible.count > 1" class="responsible-count">&nbsp;+{{ patientBalancesResponsible.count - 1 }}</span></span>
          </div>
        </template>
        <div class="legacy-widget">
          <AdminTable v-if="patientBalancesDataToday" :data="patientBalancesDataToday" :columns="patientBalancesColumns" :options="options" :extra="getExtraParams('patient-balances')" />
        </div>
      </DashboardWidget>

      <!-- Outstanding Pre-Auths Widget -->
      <DashboardWidget
        :restricted="!$can('view', 'outstanding_preauths') && !$can('view', 'all')"
        subject="outstanding_preauths"
        title="Outstanding Pre-Auths"
        icon="fa fa-clipboard"
        :is-empty="!outstandingPreAuthDataToday || outstandingPreAuthDataToday.length === 0"
        empty-icon="fa fa-clipboard"
        empty-title="No outstanding pre-auths"
        empty-text="All caught up!"
      >
        <template #header-actions>
          <div v-if="outstandingPreAuthsResponsible" class="responsible-person" v-b-tooltip.hover :title="outstandingPreAuthsResponsible.all.join(', ')">
            <span class="person-icon"><i class="fa fa-user"></i></span>
            <span>{{ outstandingPreAuthsResponsible.first }}<span v-if="outstandingPreAuthsResponsible.count > 1" class="responsible-count">&nbsp;+{{ outstandingPreAuthsResponsible.count - 1 }}</span></span>
          </div>
        </template>
        <div class="legacy-widget">
          <AdminTable v-if="outstandingPreAuthDataToday" :data="outstandingPreAuthDataToday" :columns="outstandingPreAuthColumns" :options="outstandingPreAuthOptions" :extra="getExtraParams('outstanding-preauth')" />
        </div>
      </DashboardWidget>
    </div>

   <!-- start of fourth row of data tables
   <b-row v-if="allSettings && employees">
     
     <b-col lg="6" xs="12">
         <Widget style="background-color:#e9ecef;">
          <b-row>
            <b-col xs="12">
              <h5><span class='fw-semi-bold'>Post-op follow-ups<span style="color:#218bc3;;"> (Coming Soon !)</span></span></h5>
            </b-col>
            
          </b-row>
         <table class="table mt">
           <thead>
             <tr>
               <th>
                 
               </th>
               <th>Patient</th>
               <th>Assigned to</th>
               <th>Procedure</th>
               <th>Follow-up Plan</th>               
             </tr>
           </thead>
           <tbody>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2" disabled
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>James Doe</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D4341 - Scaling and Root planing
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2" disabled
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>Mark Murphy</td>
               <td>
                  Tina Jones
               </td>
               <td>
                D7953 - Bone grafting
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2" disabled
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>John Doe</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D6010 - Implant Placement
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
            
           </tbody>
         </table>
         
       </Widget>
        
      </b-col>



      <b-col lg="6" xs="12">
        <Widget style="background-color:#e9ecef;">
          <b-row>
            <b-col><h5><span class='fw-semi-bold'>Routines<span style="color:#218bc3;;"> (Coming Soon !)</span></span></h5></b-col>
          </b-row>
         <table class="table">
           <thead>
             <tr>
               <th>
               </th>
               <th>Task</th>
               <th>Repeats</th>
               <th>Assigned to</th>
             </tr>
           </thead>
           <tbody>
             <tr>
               <td>
                <div class="abc-checkbox">
                   <input type="checkbox" disabled
                     id="checkbox1"
                   />
                   <label for="checkbox1" />
                 </div>
               </td>
               <td>Autoclave maintenance</td>
               <td>
                  <i class='glyphicon glyphicon-retweet mr'></i>Every 1st Wednesday of the month
               </td>
               <td>
                 Christina Jones
               </td>
             </tr>
             <tr>
               <td>
                <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox1" disabled
                   />
                   <label for="checkbox1" />
                 </div>
               </td>
               <td>Changing out Suction Valves</td>
               <td>
                  <i class='glyphicon glyphicon-retweet mr'></i>Every Monday
               </td>
               <td>
                 Christina Jones
               </td>
             </tr>
             <tr>
               <td>
                <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox1" disabled
                   />
                   <label for="checkbox1" />
                 </div>
               </td>
               <td>Update Order List </td>
               <td>
                  <i class='glyphicon glyphicon-retweet mr'></i>Every other Wednesday
               </td>
               <td>
                 Nicole Adams
               </td>
             </tr>
           </tbody>
         </table>
         
       </Widget>
     </b-col>

   </b-row>   
   -->
   
   <!-- start of fifth row of data tables

   <b-row v-if="allSettings && employees">
     <b-col lg="6" xs="12">
        <Widget>
          <b-row>
            <b-col xs="7">
              <h5><span class='fw-semi-bold'>Premed Reminders</span></h5>
            </b-col>
            
            <b-col xs="5">
              <div class="post-user mt-n-sm">
                  
                  <span class="thumb-xs mr mt-n-sm"> 
                    <img class="rounded-circle" src='../../assets/people/a4.jpg' alt="..." />
                  </span>
                  <p class="fw-semi-bold mb-xs pull-right ">Maryna Nison</p>
                  <p class="fs-mini text-muted mb-0"><small>Treatment Coordinator</small></p>

              </div>
            </b-col>
          </b-row>
         <table class="table mt">
           <thead>
             <tr>
               <th>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox1"
                   />
                   <label for="checkbox1" />
                 </div>
               </th>
               <th>Patient</th>
               <th>Provider</th>
               <th>Appointment Description</th>
               <th>Status</th>
             </tr>
           </thead>
           <tbody>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2"
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>Mark</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D4341 - Scaling and Root planing
               </td>
               <td>
                  <div><span class="badge badge-secondary">Left Msg on Ans. Mach</span></div>
               </td>
               
             </tr>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2"
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>Mark</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D4341 - Scaling and Root planing
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
             <tr>
               <td>
                 <div class="abc-checkbox">
                   <input type="checkbox"
                     id="checkbox2"
                   />
                   <label for="checkbox2" />
                 </div>
               </td>
               <td>Mark</td>
               <td>
                  Nilay Shah
               </td>
               <td>
                 D4341 - Scaling and Root planing
               </td>
               <td>
                  <div ><span class="badge text-white badge-danger badge-pill">Due Today</span></div>
               </td>
               
             </tr>
            
           </tbody>
         </table>
         
       </Widget>
     </b-col>
     
   </b-row>   
   
  -->

  </div>
</template>

<script>
/* eslint-disable */
  import Vue from 'vue';
  import Widget from '@/components/Widget/Widget';
  import { mapState, mapActions, mapMutations, mapGetters } from 'vuex';
  import AdminPage2Skeleton from './AdminPage2Skeleton.vue';
  import { canUseConversations } from '@/utils/siteCapability';
  import mixin from '../../mixin.js';
  import AdminTable from './AdminTable';
  import moment from 'moment-timezone';
  import _ from 'lodash';
  import TodoItem from '../TaskList/components/TodoItem';
  import TodoDetails from '../TaskList/components/TodoDetails';
  import { getResponsibleEmployees } from '@/utils/permissions';

  // New Dashboard Components
  import DashboardWidget from './components/DashboardWidget.vue';
  import UnreadMessagesWidget from './components/UnreadMessagesWidget.vue';
  import NewFormsWidget from './components/NewFormsWidget.vue';
  import FeaturePreview from '@/components/FeaturePreview';
  import DashboardGrid from './components/DashboardGrid.vue';
  import DashboardToolbar from './components/DashboardToolbar.vue';
  import HeroStatsBar from './components/HeroStatsBar.vue';

  export default {
    name: 'AdminPage',
    components: {
      Widget,
      AdminPage2Skeleton,
      AdminTable,
      TodoItem,
      DashboardWidget,
      UnreadMessagesWidget,
      NewFormsWidget,
      FeaturePreview,
      DashboardGrid,
      DashboardToolbar,
      HeroStatsBar
    },
    mixins: [mixin],

    mounted() {
      //this.mountSlick()
      this.fetchToDos();
      // Load hidden hero stats from localStorage
      this.loadHiddenHeroStats();
      // Set dashboard grid ref after mount for toolbar integration
      this.$nextTick(() => {
        this.dashboardGridRef = this.$refs.dashboardGrid;
      });
    },

    created() {      
      if(this.allSettings)
        this.init();
    },

    data() {
      return {
        busy: false,
        results: [],
        selectedPatients: [],
        // Dashboard grid reference (set after mount for toolbar integration)
        dashboardGridRef: null,
        // Hidden hero stats (persisted to localStorage)
        hiddenHeroStats: [],
        // Accordion expand/collapse state - sections with items start expanded
        expandedSections: {
          tasks: true,
          messages: true,
          forms: true,
          broken: true,
          recall: true,
          treatment: false,
          claims: false,
          balances: false,
          preauths: false
        },
        newFormsCount: 0, // Will be updated by NewFormsWidget
        options: {
          filterable: [],
          skin:'table',
          sortable: [],
          perPage: 10,
          sortByColumn: false,
          pagination: { chunk: 10, dropdown: false },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          }
          //columnsClasses: {id:'w'}
        },
        outstandingClaimsOptions: {
          skin:'table',
          sortable: [],
          perPage: 10,
          filterByColumn: false,
          filterable: ['Patient', 'Carrier'],
          sortByColumn: false,
          pagination: { chunk: 10, dropdown: false },
          texts: {
            filter: 'Filter by Patient or Carrier'
            //count:' '
          },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          }
        },
        outstandingPreAuthOptions: {
          skin:'table',
          sortable: [],
          perPage: 10,
          filterByColumn: false,
          filterable: ['Patient', 'Carrier'],
          sortByColumn: false,
          pagination: { chunk: 10, dropdown: false },
          texts: {
            filter: 'Filter by Patient or Carrier'
            //count:' '
          },
          rowClassCallback: function(row) {
            return row.teamioActive ? 'selectedRow' : '';
          }
        },
        allMyTasksLoadingStatus: [].repeat(false, 30),
        wikiPages: [],
        mytasksColumns: ['id', 'taskContent'],
        unscheduledTreatmentColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        brokenAppointmentsColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        receivedPreAuthColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        outstandingClaimsColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        outstandingPreAuthColumns: ['id', 'Patient', 'FollowUpActionPlan', 'Attempts'],
        patientBalancesColumns: ['id', 'Patient', 'amount', 'FollowUpActionPlan', 'Attempts'],
        postOpFollowUpsColumns: ['id', 'Patient', 'Amount', 'FollowUpActionPlan', 'Attempts'],
        preMedRemindersColumns: ['id', 'Patient', 'Amount', 'FollowUpActionPlan', 'Attempts'],
        routinesColumns: []
      }
    },

    methods: {
      ...mapActions('teamio', ['runQuery']),
      ...mapActions('wiki', ['getWikiPages', 'getSiteTags']),
      ...mapActions('tasks', ['getTasksForEmployee', 'getTasksForSite', 'getTaskDetails', 'addPersonalTask', 'updateTask', 'updateMultipleTasks', 'deleteTask', 'deleteThisInstanceOfRepeatedTask']),
      ...mapMutations('teamio', ['emptyResults']),

      // Shared helper for every xxxResponsible computed. Returns
      // {first, all, count} or null. See utils/permissions.getResponsibleEmployees.
      _responsibleEmployeesFor(settingKey) {
        if (!this.allSettings || !this.allSettings[settingKey]) return null;
        return getResponsibleEmployees(this.employees, this.allSettings[settingKey].PropertyValue);
      },

      // Dashboard Grid wrapper methods for toolbar integration
      onGridSizeChange(size) {
        if (this.$refs.dashboardGrid) {
          this.$refs.dashboardGrid.handleSizeChange(size);
        }
      },
      onGridAddWidget(widgetId) {
        if (this.$refs.dashboardGrid) {
          this.$refs.dashboardGrid.handleAddWidget(widgetId);
        }
      },
      onGridAddChart(chartId) {
        if (this.$refs.dashboardGrid) {
          this.$refs.dashboardGrid.handleAddChart(chartId);
        }
      },
      onGridResetLayout() {
        if (this.$refs.dashboardGrid) {
          this.$refs.dashboardGrid.handleResetLayout();
        }
        // Also restore all hidden hero stats
        this.hiddenHeroStats = [];
        this.saveHiddenHeroStats();
      },

      // Restore a hidden hero stat
      onRestoreHeroStat(statKey) {
        const index = this.hiddenHeroStats.indexOf(statKey);
        if (index > -1) {
          this.hiddenHeroStats.splice(index, 1);
          this.saveHiddenHeroStats();
        }
      },

      // Hero Stats click handler
      handleStatClick(statKey) {
        // Navigate or scroll to the relevant section
        console.log('Stat clicked:', statKey);
        // Could implement scrolling to relevant widget or opening a modal
      },

      // Hero Stats order change handler (Tier 1 drag)
      handleStatsOrderChange(newOrder) {
        // Save the new order to localStorage via DashboardGrid's layout
        if (this.$refs.dashboardGrid && this.$refs.dashboardGrid.layout) {
          const layout = this.$refs.dashboardGrid.layout;
          // Update stats order in layout
          newOrder.forEach((statKey, index) => {
            const stat = layout.stats.find(s => s.id === statKey);
            if (stat) {
              stat.order = index;
            }
          });
          // Persist the layout
          this.$refs.dashboardGrid.persistLayout();
        }
        console.log('Stats order changed:', newOrder);
      },

      // Hero Stats remove handler (Tier 1 hide)
      handleStatRemove(statKey) {
        // Add to hidden stats array if not already there
        if (!this.hiddenHeroStats.includes(statKey)) {
          this.hiddenHeroStats.push(statKey);
          // Persist to localStorage
          this.saveHiddenHeroStats();
        }
        console.log('Stat hidden:', statKey);
      },

      // Save hidden hero stats to localStorage
      saveHiddenHeroStats() {
        try {
          const storageKey = `dashboard-hidden-stats-${this.siteID}`;
          localStorage.setItem(storageKey, JSON.stringify(this.hiddenHeroStats));
        } catch (e) {
          console.error('Error saving hidden stats:', e);
        }
      },

      // Load hidden hero stats from localStorage
      loadHiddenHeroStats() {
        try {
          const storageKey = `dashboard-hidden-stats-${this.siteID}`;
          const saved = localStorage.getItem(storageKey);
          if (saved) {
            this.hiddenHeroStats = JSON.parse(saved);
          }
        } catch (e) {
          console.error('Error loading hidden stats:', e);
          this.hiddenHeroStats = [];
        }
      },

      // Chart period change handler
      handlePeriodChange(period) {
        console.log('Period changed:', period);
        // Could fetch new data for the selected period
      },

      // View all activity handler
      handleViewAllActivity() {
        // Navigate to activity log or open modal
        console.log('View all activity');
      },

      // Activity action handler
      handleActivityAction({ activity, action }) {
        console.log('Activity action:', action.key, 'on', activity.type, activity.id);
        // Additional custom action handling can be added here
      },

      // Campaign item click handler (for sample data)
      handleCampaignItemClick(item) {
        console.log('Campaign item clicked:', item.name);
        // Navigate to campaigns page
        this.$router.push({ name: 'campaigns' });
      },

      async init() {
        this.busy = true;
        let query = {
          type: 'ADMIN_DASHBOARD',
          siteID: this.siteID,
          d: moment().format('YYYY-MM-DD')
        };
        this.emptyResults();
        try {
          this.results = await this.runQuery(query);
          let temp = await this.getWikiPages({siteID: this.siteID});
          if(temp.length > 0)
            this.wikiPages = temp[0];
        } finally {
          this.busy = false;
        }
      },

      async fetchToDos () {
        this.busy = true;
        try {
          if(this.myCreatedTasks.length === 0 || this.assignedToMeTasks.length === 0 || this.myEmployeesTasks.length === 0) {
            if(this.user.Role === 'Super Admin' || this.user.Role === 'Admin') {
              await this.getTasksForSite({siteID: this.siteID, employeeID: this.userID});
            } else {
              await this.getTasksForEmployee({siteID: this.siteID, employeeID: this.userID});
            }
          }
        } finally {
          this.busy = false;
        }
      },

      // Toggle accordion section expand/collapse
      toggleSection(sectionKey) {
        this.expandedSections[sectionKey] = !this.expandedSections[sectionKey];
      },

      // Update forms count from child widget
      updateFormsCount(count) {
        this.newFormsCount = count;
      },

      async deleteTaskItem(item) {
        //console.log(`trying to delete item: ${JSON.stringify(item)}`);
        await this.deleteTask(
          { 
            id: item.id,
            deleted: true,
            deletedBy: this.userID,
            deletedOn: moment().toDate(),
          });
      },

      async deleteThisInstanceOnly(item) {
        let payload = {
          task : item,
          //clientDateFormat : this.allSettings.GENERAL_TIMEZONE.PropertyValue.toUpperCase() === 'AFRICA/MASERU' ? 'DD/MM/YYYY' : 'MM/DD/YYYY'
          clientDateFormat: this.dateFormatStr || 'MM/DD/YYYY'
        }
        //console.log(`payload: ${JSON.stringify(payload)}`);
        await this.deleteThisInstanceOfRepeatedTask(payload);
      },

      async editTaskItem(item, index, taskType) {        
        console.log('i am in edit item', item, index, taskType);
        let temp;
        switch(taskType) {
          case 'allMyTasksDueToday': temp = this.allMyTasksLoadingStatus; break;
        }

        Vue.set(temp, index, true);
        let task;
        try {
          task = await this.getTaskDetails({taskID: item.id});
          //console.log(`task details: ${JSON.stringify(task)}`);
          //console.log(`got task details`);

          if(!task.assignees) {
            let temp2 = [];    
            temp2.push(task.assignedToEmpID);
            Vue.set(task, 'assignees', temp2);
          }

          /*
            Adding a patch because I noticed sometimes, some sites have some tasks that have wrong completedAt date and that messes up when they try to mark them completed
          */
          if(task.completedAt) {
            try {
              let temp = task.completedAt.toISOString();
              //console.log('able to convert completedAt to ISO String. No issue', temp);
            } catch (error) {
              // if we catch an error here then we have an invalid date here. let's remove it
              delete task.completedAt;
              delete task.completedAtStr;
            }
          }

          const panel1Handle = this.$showPanel({
            component : TodoDetails,
            openOn: 'right',
            width: 750,
            props: {
              task: task,
              wikiPages: this.wikiPages,
              patients: this.patients,
            }
          })

          panel1Handle.promise
            .then(result => {
              //console.log(`panel handle promise done`);
              Vue.set(temp, index, false);
          });
        } catch(error) {
          console.log(`Error while trying to edit task: ${error}`);
          Vue.set(temp, index, true);
        }
      },

      async handleItemChange (item) {        
        console.log('in handleItemChange', item);
        /*
          Adding a patch because I noticed sometimes, some sites have some tasks that have wrong completedAt date and that messes up when they try to mark them completed
        */
        if(item.completedAt) {
          try {
            let temp = item.completedAt.toISOString();
            //console.log('able to convert completedAt to ISO String. No issue', temp);
          } catch (error) {
            // if we catch an error here then we have an invalid date here. let's remove it
            delete item.completedAt;
            delete item.completedAtStr;
          }
        }
        await this.updateTask(item);
      },
    },

    computed: {
      ...mapState({
         patients: state => state.customer.patients,
         myCreatedTasks: state => state.tasks.myCreatedTasks,
         assignedToMeTasks: state => state.tasks.assignedToMeTasks,
         myEmployeesTasks: state => state.tasks.myEmployeesTasks,
         conversationRooms: state => state.conversationsV2.rooms
      }),

      ...mapGetters({
        'allMyTasksDueToday' : 'tasks/allMyTasksDueToday',
        'todaysTasksForEmployee': 'tasks/todaysTasksForEmployee'
      }),

      // Greeting message based on time of day
      greetingMessage() {
        const hour = moment().hour();
        const userName = this.user?.Name?.split(' ')[0] || 'there';
        if (hour < 12) return `Good Morning, ${userName}`;
        if (hour < 17) return `Good Afternoon, ${userName}`;
        return `Good Evening, ${userName}`;
      },

      // Today's date formatted
      todayDateFormatted() {
        return moment().format('dddd, MMMM D, YYYY');
      },

      // Hero Stats for the KPI bar
      // Keys match STATS_REGISTRY in dashboardConfig.js for consistency
      heroStats() {
        const stats = [];

        // Today's Appointments
        const todayAppts = this.appointmentsData ? this.appointmentsData.reduce((a, b) => a + b, 0) / 7 : 0;
        const todayApptsValue = Math.round(todayAppts);
        stats.push({
          key: 'todayAppointments',
          label: "Today's Appointments",
          value: todayApptsValue,
          icon: 'fa fa-calendar-check-o',
          color: 'blue',
          urgency: 'info',
          clickable: true,
          showSparkline: true,
          subtitle: todayApptsValue === 0 ? 'No appointments' : `${todayApptsValue} scheduled`
        });

        // Tasks due today
        const tasksDueToday = this.todaysTasksForEmployee ? this.todaysTasksForEmployee(this.userID) : [];
        const pendingTasks = tasksDueToday ? tasksDueToday.filter(t => !t.completed).length : 0;
        stats.push({
          key: 'tasksToday',
          label: 'Tasks Due Today',
          value: pendingTasks,
          icon: 'fa fa-check-square-o',
          color: 'orange',
          urgency: pendingTasks > 5 ? 'warning' : pendingTasks > 0 ? 'info' : 'success',
          clickable: true,
          showSparkline: true,
          subtitle: pendingTasks === 0 ? 'All done!' : `${pendingTasks} pending`
        });

        // Unread messages
        const unreadMessages = this.conversationRooms
          ? this.conversationRooms.filter(r => parseInt(r.unreadCount) > 0).length
          : 0;
        stats.push({
          key: 'unreadMessages',
          label: 'Unread Messages',
          value: unreadMessages,
          icon: 'fa fa-comment-o',
          color: 'teal',
          urgency: unreadMessages > 0 ? 'info' : 'success',
          clickable: true,
          showSparkline: false,
          subtitle: unreadMessages === 0 ? 'Inbox clear' : `${unreadMessages} conversations`
        });

        // Form Submissions
        const formSubmissions = this.newFormsCount || 0;
        stats.push({
          key: 'newForms',
          label: 'Form Submissions',
          value: formSubmissions,
          icon: 'fa fa-file-text-o',
          color: 'green',
          urgency: formSubmissions > 5 ? 'info' : formSubmissions > 0 ? 'success' : 'neutral',
          clickable: true,
          showSparkline: true,
          subtitle: formSubmissions === 0 ? 'No new submissions' : `${formSubmissions} to review`
        });

        // Overdue Recall
        const overdueRecall = this.overdueRecallDataToday ? this.overdueRecallDataToday.filter(r => !r.completed).length : 0;
        stats.push({
          key: 'overdueRecall',
          label: 'Overdue Recall',
          value: overdueRecall,
          icon: 'fa fa-clock-o',
          color: 'red',
          urgency: overdueRecall > 10 ? 'urgent' : overdueRecall > 0 ? 'warning' : 'success',
          clickable: true,
          showSparkline: true,
          subtitle: overdueRecall === 0 ? 'Caught up!' : `${overdueRecall} to call`
        });

        // Filter out hidden stats
        return stats.filter(stat => !this.hiddenHeroStats.includes(stat.key));
      },

      // Hidden hero stats with metadata (for Add Widget dropdown)
      hiddenHeroStatsWithMeta() {
        // Define all possible stats with their metadata
        const allStatsMeta = {
          todayAppointments: { key: 'todayAppointments', label: "Today's Appointments", icon: 'fa fa-calendar-check-o' },
          tasksToday: { key: 'tasksToday', label: 'Tasks Due Today', icon: 'fa fa-check-square-o' },
          unreadMessages: { key: 'unreadMessages', label: 'Unread Messages', icon: 'fa fa-comment-o' },
          newForms: { key: 'newForms', label: 'Form Submissions', icon: 'fa fa-file-text-o' },
          overdueRecall: { key: 'overdueRecall', label: 'Overdue Recall', icon: 'fa fa-clock-o' }
        };

        // Return metadata for hidden stats only
        return this.hiddenHeroStats
          .filter(key => allStatsMeta[key])
          .map(key => allStatsMeta[key]);
      },

      // Weekly activity data for trend chart
      weeklyActivityData() {
        // Sample data - in real implementation, this would come from backend
        return [12, 19, 15, 25, 22, 30, 28];
      },

      weeklyLabels() {
        const days = [];
        for (let i = 6; i >= 0; i--) {
          days.push(moment().subtract(i, 'days').format('ddd'));
        }
        return days;
      },

      totalWeeklyActivity() {
        return this.weeklyActivityData.reduce((a, b) => a + b, 0);
      },

      weeklyTrend() {
        // Calculate trend vs last week (sample: +12%)
        return 12;
      },

      // Appointments data for trend chart
      appointmentsData() {
        // Sample data
        return [8, 12, 10, 15, 11, 9, 14];
      },

      totalAppointments() {
        return this.appointmentsData.reduce((a, b) => a + b, 0);
      },

      appointmentsTrend() {
        return 8;
      },

      // Activity inline stats (like Campaigns: Active, Scheduled, Drafts)
      activityInlineStats() {
        // Sample inline stats - in real implementation, this would come from backend
        return [
          { value: '12', label: 'Tasks' },
          { value: '8', label: 'Calls' },
          { value: '5', label: 'Pending' }
        ];
      },

      // Recent activity items for the chart section
      recentActivity() {
        // Sample data - in real implementation, this would come from tasks/calls backend
        return [
          {
            name: 'Follow up with John Smith',
            meta: 'Completed 2 hours ago',
            icon: 'fa fa-check-circle',
            badge: 'Done',
            badgeClass: 'badge-success',
            clickable: true
          },
          {
            name: 'Called Mary Johnson',
            meta: 'Call completed 4 hours ago',
            icon: 'fa fa-phone',
            badge: 'Call',
            badgeClass: 'badge-info',
            clickable: true
          },
          {
            name: 'Send treatment plan to patient',
            meta: 'Due today',
            icon: 'fa fa-clock-o',
            badge: 'Pending',
            badgeClass: 'badge-warning',
            clickable: true
          }
        ];
      },

      // Appointments inline stats
      appointmentsInlineStats() {
        // Sample inline stats
        return [
          { value: '5', label: 'Today' },
          { value: '23', label: 'This Week' },
          { value: '3', label: 'Pending' }
        ];
      },

      // Recent/upcoming appointments for the chart section
      recentAppointments() {
        // Sample data - in real implementation, this would come from scheduling backend
        return [
          {
            name: 'John Smith - Cleaning',
            meta: 'Today at 10:00 AM',
            icon: 'fa fa-calendar-check-o',
            badge: 'Confirmed',
            badgeClass: 'badge-success',
            clickable: true
          },
          {
            name: 'Mary Johnson - Crown',
            meta: 'Today at 2:30 PM',
            icon: 'fa fa-calendar',
            badge: 'Scheduled',
            badgeClass: 'badge-info',
            clickable: true
          },
          {
            name: 'Robert Davis - Checkup',
            meta: 'Tomorrow at 9:00 AM',
            icon: 'fa fa-calendar',
            badge: 'Scheduled',
            badgeClass: 'badge-info',
            clickable: true
          }
        ];
      },

      // Campaigns data for trend chart
      campaignsData() {
        // Sample data - in real implementation, this would come from backend
        return [5, 12, 8, 15, 10, 18, 14];
      },

      totalCampaignsSent() {
        return this.campaignsData.reduce((a, b) => a + b, 0);
      },

      campaignsTrend() {
        // Calculate trend vs last week (sample: +15%)
        return 15;
      },

      // Sample recent campaigns for the chart section (used when no real data)
      sampleRecentCampaigns() {
        // Sample data - in real implementation, this would come from campaigns backend
        return [
          {
            name: 'Holiday Special Offer',
            meta: '2,450 recipients • Sent 2 days ago',
            icon: 'fa fa-envelope',
            badge: 'Completed',
            badgeClass: 'status-completed',
            avatarClass: 'status-completed',
            clickable: true
          },
          {
            name: 'Appointment Reminder',
            meta: '1,200 recipients • Scheduled for tomorrow',
            icon: 'fa fa-mobile',
            badge: 'Scheduled',
            badgeClass: 'status-scheduled',
            avatarClass: 'status-scheduled',
            clickable: true
          },
          {
            name: 'New Patient Welcome',
            meta: '850 recipients • Active',
            icon: 'fa fa-paper-plane',
            badge: 'Active',
            badgeClass: 'status-active',
            avatarClass: 'status-active',
            clickable: true
          }
        ];
      },

      // Feature checks
      messagingFeatureEnabled() {
        // Per-site SMS capability — src/utils/siteCapability.js (single implementation).
        // Now returns a real boolean; the old expression could return undefined/'' when
        // settings were absent, which is falsy but not `false`.
        // canUseConversations: the Messages widget is the inbox, which a WhatsApp-only office keeps.
        return canUseConversations(this.allSettings);
      },

      formsPaidFor() {
        // Check if Forms feature is paid for
        if (this.allSettings && this.allSettings.FORMS_PAID_FOR &&
            this.allSettings.FORMS_PAID_FOR.PropertyValue === 'TRUE') {
          return true;
        }
        return false;
      },

      // Show side-by-side grid when both messages and forms have nothing to show
      showMessagesFormsGrid() {
        const messagesEmpty = !this.messagingFeatureEnabled || this.unreadMessagesCount === 0;
        const formsEmpty = !this.formsPaidFor || this.newFormsCount === 0;
        return messagesEmpty && formsEmpty;
      },

      // Accordion section item counts
      myTasksCount() {
        const tasks = this.todaysTasksForEmployee ? this.todaysTasksForEmployee(this.userID) : [];
        return tasks ? tasks.filter(t => !t.completed).length : 0;
      },

      unreadMessagesCount() {
        return this.conversationRooms
          ? this.conversationRooms.filter(r => parseInt(r.unreadCount) > 0).length
          : 0;
      },

      brokenApptsCount() {
        return this.brokenAppointmentsDataToday
          ? this.brokenAppointmentsDataToday.filter(b => !b.completed).length
          : 0;
      },

      overdueRecallCount() {
        return this.overdueRecallDataToday
          ? this.overdueRecallDataToday.filter(r => !r.completed).length
          : 0;
      },

      unscheduledTreatmentCount() {
        return this.unscheduledTreatmentDataToday
          ? this.unscheduledTreatmentDataToday.filter(t => !t.completed).length
          : 0;
      },

      outstandingClaimsCount() {
        return this.outstandingClaimsDataToday
          ? this.outstandingClaimsDataToday.filter(c => !c.completed).length
          : 0;
      },

      patientBalancesCount() {
        return this.patientBalancesDataToday
          ? this.patientBalancesDataToday.filter(b => !b.completed).length
          : 0;
      },

      outstandingPreAuthCount() {
        return this.outstandingPreAuthDataToday
          ? this.outstandingPreAuthDataToday.filter(p => !p.completed).length
          : 0;
      },

      // Dashboard Stats for the stats bar
      // Using Font Awesome 4.7 icon names
      dashboardStats() {
        const stats = [];

        // Tasks due today
        const tasksDueToday = this.todaysTasksForEmployee ? this.todaysTasksForEmployee(this.userID) : [];
        const overdueTasks = tasksDueToday ? tasksDueToday.filter(t => !t.completed).length : 0;
        stats.push({
          key: 'tasks',
          label: 'Tasks Today',
          value: overdueTasks,
          icon: 'fa fa-check-square-o',
          urgency: overdueTasks > 0 ? 'warning' : 'success'
        });

        // Unread messages
        const unreadMessages = this.conversationRooms
          ? this.conversationRooms.filter(r => parseInt(r.unreadCount) > 0).length
          : 0;
        stats.push({
          key: 'messages',
          label: 'Unread Messages',
          value: unreadMessages,
          icon: 'fa fa-comments',
          urgency: unreadMessages > 0 ? 'info' : 'success'
        });

        // Broken Appointments
        const brokenAppts = this.brokenAppointmentsDataToday ? this.brokenAppointmentsDataToday.filter(b => !b.completed).length : 0;
        stats.push({
          key: 'broken',
          label: 'Broken Appts',
          value: brokenAppts,
          icon: 'fa fa-calendar-times-o',
          urgency: brokenAppts > 0 ? 'urgent' : 'success'
        });

        // Overdue Recall
        const overdueRecall = this.overdueRecallDataToday ? this.overdueRecallDataToday.filter(r => !r.completed).length : 0;
        stats.push({
          key: 'recall',
          label: 'Overdue Recall',
          value: overdueRecall,
          icon: 'fa fa-phone',
          urgency: overdueRecall > 0 ? 'warning' : 'success'
        });

        // Outstanding Claims
        const outstandingClaims = this.outstandingClaimsDataToday ? this.outstandingClaimsDataToday.filter(c => !c.completed).length : 0;
        stats.push({
          key: 'claims',
          label: 'Claims',
          value: outstandingClaims,
          icon: 'fa fa-file-text-o',
          urgency: outstandingClaims > 5 ? 'warning' : 'neutral'
        });

        return stats;
      },

      adminData() {
        if(this.results && this.results.data && this.results.data.length > 0)
          return this.results.data;
      },

      overdueRecallDataToday() {
        if(this.adminData) {
           let temp = this.adminData[0].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.OverdueRecallStatusUpdatedAt).isSame(moment(), 'day') ||  t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
             /* The reason for the check below is because when the data comes from DB, it's string and then i convert to boolean locally. 
             So if I need to check the value again, I need to first check if it's boolean or not */
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },

      overdueRecallDataPercentCompleted() {
        if(this.adminData && this.allSettings) {
          return ((this.adminData[0].filter(t => t.completed === true).length /this.allSettings.OVERDUE_RECALL_DAILY_CALLS_GOAL.PropertyValue)*100).toFixed(0);
        }
      },

      unscheduledTreatmentDataToday() {
        if(this.adminData) {
           let temp = this.adminData[1].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.UnscheduledTreatmentStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },

      unscheduledTreatmentDataPercentCompleted() {
        if(this.adminData)
          return ((this.adminData[1].filter(t => t.completed === true).length/this.allSettings.UNSCHEDULED_TREATMENT_DAILY_CALLS_GOAL.PropertyValue)*100).toFixed(0);
      },

      brokenAppointmentsDataToday() {        
        if(this.adminData) {
          //console.log(`brokenAppointmentsDataToday called with data length ${this.adminData[2].length}: ${JSON.stringify(this.adminData[2], null, 2)}`);
           let temp = this.adminData[2].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.BrokenAppointmentStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },

      outstandingClaimsDataToday() {
        if(this.adminData) {
           let temp = this.adminData[3].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.OutstandingClaimStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        } 
      },

      receivedPreAuthDataToday() {
        if(this.adminData) {
           let temp = this.adminData[4].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.ProcessedPreAuthStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },
      
      outstandingPreAuthDataToday() {
        if(this.adminData) {
           let temp = this.adminData[5].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.OutstandingPreAuthStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }  
      },

      patientBalancesDataToday() {
        if(this.adminData) {
           let temp = this.adminData[6].filter(t => moment(t.CreatedAt).isSame(moment(), 'day') || moment(t.PatientBalancesStatusUpdatedAt).isSame(moment(), 'day') || t.dueToday === 'true' || t.pastDue === 'true');
           temp.forEach(function(item) {
              if(typeof item['completed'] !== "boolean")
                item['completed'] = item['completed'] === 'false' ? false : true;   
           });   
           return temp;
        }
      },

      // Each *_RESPONSIBLE_EMPLOYEEID setting holds a pipe-delimited list of
      // EmployeeIDs ("empA|empB|empC"). The old code used `===` against the
      // whole pipe-string, which only matched for single-employee setups and
      // silently went blank everywhere else. The new helper returns
      // {first, all, count} so the template can render "first name + N more"
      // with a tooltip listing every responsible employee. Also fixes two
      // wrong setting keys (PROCESSED_PREAUTHS_EMPLOYEEID and
      // OUTSTANDING_PREAUTHS_EMPLOYEEID were missing the RESPONSIBLE_ prefix —
      // those computed properties were ALWAYS undefined before this).
      overdueRecallResponsible() {
        return this._responsibleEmployeesFor('OVERDUE_RECALL_RESPONSIBLE_EMPLOYEEID');
      },

      unscheduledTreatmentResponsible() {
        return this._responsibleEmployeesFor('UNSCHEDULED_TREATMENT_RESPONSIBLE_EMPLOYEEID');
      },

      brokenAppointmentResponsible() {
        return this._responsibleEmployeesFor('BROKEN_APPOINTMENTS_RESPONSIBLE_EMPLOYEEID');
      },

      outstandingClaimResponsible() {
        return this._responsibleEmployeesFor('OUTSTANDING_CLAIM_RESPONSIBLE_EMPLOYEEID');
      },

      patientBalancesResponsible() {
        return this._responsibleEmployeesFor('PATIENT_BALANCES_RESPONSIBLE_EMPLOYEEID');
      },

      processedPreAuthsResponsible() {
        return this._responsibleEmployeesFor('PROCESSED_PREAUTHS_RESPONSIBLE_EMPLOYEEID');
      },

      outstandingPreAuthsResponsible() {
        return this._responsibleEmployeesFor('OUTSTANDING_PREAUTHS_RESPONSIBLE_EMPLOYEEID');
      },

      overdueRecallColumns() {
        let cols = ['id', 'Patient'];
        if (
          this.allSettings &&
          this.allSettings.APPT_REMINDER_PAID_FOR &&
          this.allSettings.APPT_REMINDER_PAID_FOR.PropertyValue === 'TRUE'
        ) {
          cols.push('AutoRemindersSent');
        }
        cols.push('FollowUpActionPlan', 'Attempts');
        return cols;
      },
    },

    watch: {
      allSettings(newValue, oldValue) {
        //console.log(`watch changed. Old value: ${JSON.stringify(oldValue)}, \n New value: ${JSON.stringify(newValue)}`);
        if(!oldValue) {
          this.init();
        }
        // Set dashboard grid ref when allSettings becomes available (grid renders conditionally)
        if (newValue && !this.dashboardGridRef) {
          this.$nextTick(() => {
            this.dashboardGridRef = this.$refs.dashboardGrid;
          });
        }
      }
    }

  };
</script>

<!-- Modern Dashboard Styles -->
<style src="./AdminPageModern.scss" lang="scss" />

<!-- Legacy styles for AdminTable compatibility -->
<style src="./Basic.scss" lang="scss" />
